
import React, { useState } from 'react';
import { safeClipboardWrite } from '../utils/safeStorage';
import { Participant, ParticipantInvitation, Project, ParticipantGroup } from '../types';
import { PlusIcon, SendIcon, FolderPlusIcon, UsersIcon } from './icons';
import AddParticipantsModal from './AddParticipantsModal';

import GroupCard from './GroupCard';
import ConfirmationModal from './ConfirmationModal';
import Avatar from './Avatar';

interface ParticipantsViewProps {
  participants: Participant[];
  invitations: ParticipantInvitation[];
  groups: ParticipantGroup[];
  onInvite: (emails: string[]) => void;
  onRemove: (participantId: string) => void;
  onRevoke: (inviteId: string) => void;
  onSaveGroup: (group: ParticipantGroup) => void;
  onDeleteGroup: (groupId: string) => void;
  currentProject: Project;
  onViewProfile: (participant: Participant) => void;
  onOpenGroupModal: (group?: ParticipantGroup) => void;
}

const ParticipantsView: React.FC<ParticipantsViewProps> = ({
  participants,
  invitations,
  groups,
  onInvite,
  onRemove,
  onRevoke,
  onSaveGroup,
  onDeleteGroup,
  currentProject,
  onViewProfile,
  onOpenGroupModal
}) => {
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'group' | 'participant' | 'invite', id: string, name: string } | null>(null);



  const handleDeleteClick = (type: 'group' | 'participant' | 'invite', id: string, name: string) => {
    setItemToDelete({ type, id, name });
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!itemToDelete) return;

    switch (itemToDelete.type) {
      case 'group':
        onDeleteGroup(itemToDelete.id);
        break;
      case 'participant':
        onRemove(itemToDelete.id);
        break;
      case 'invite':
        onRevoke(itemToDelete.id);
        break;
    }

    setIsDeleteModalOpen(false);
    setItemToDelete(null);
  };

  // `?ref=invite` is a permanent cache-bust for WhatsApp/Meta link
  // unfurling. The bare /join/<id> URL gets its OG preview cached by
  // Meta keyed on the exact string; if Meta ever scrapes it during a
  // deploy / cold window it caches an empty result and keeps serving
  // it for days. Appending a stable suffix yields a URL Meta has
  // never cached → one fresh scrape against the (correct) server →
  // good card cached for that URL from then on. The participant join
  // route matches on pathname only (App.tsx `/^\/join\/(.+)$/`), so
  // the query param is ignored by app logic — purely a share-surface
  // concern. Stable (not a timestamp) so re-copies reuse the good
  // cached scrape rather than re-scraping endlessly.
  // `?ref=invite` — permanent WhatsApp/Meta OG-cache bust (see above
  //   rationale; a stable suffix yields a URL Meta scrapes fresh once
  //   then caches good).
  // `&theme=glass` — invited participants must land in the glass UI.
  //   index.html's pre-paint bootstrap reads ?theme=glass and persists
  //   it to localStorage.dekko-theme, so it sticks through the
  //   sign-out → /p → onboarding navigation (client-side route change;
  //   localStorage survives). Without it a brand-new user has empty
  //   localStorage and falls through to the classic/reskin default.
  //   Deliberately NOT flipping the global index.html default — that's
  //   the separate parked "promote glass to default" decision.
  const projectLink = `${window.location.origin}/join/${currentProject.id}?ref=invite&theme=glass`;

  const handleCopyLink = () => {
    safeClipboardWrite(projectLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const getDeleteModalContent = () => {
    if (!itemToDelete) return { title: '', message: '', confirmText: '' };
    switch (itemToDelete.type) {
      case 'group':
        return {
          title: 'Delete Group',
          message: `Are you sure you want to delete the group "${itemToDelete.name}"? This action only removes the group, not the participants within it.`,
          confirmText: 'Delete Group'
        };
      case 'participant':
        return {
          title: 'Remove Participant',
          message: `Are you sure you want to remove "${itemToDelete.name}" from this project? This will remove their access and all their associated data. This action cannot be undone.`,
          confirmText: 'Remove Participant'
        };
      case 'invite':
        return {
          title: 'Revoke Invitation',
          message: `Are you sure you want to revoke the invitation for "${itemToDelete.name}"? They will no longer be able to join the project with this invite.`,
          confirmText: 'Revoke Invitation'
        };
      default:
        return { title: '', message: '', confirmText: '' };
    }
  };

  const deleteModalContent = getDeleteModalContent();

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold text-text-ink">Participants & Groups</h2>
          <p className="text-sm text-text-muted mt-1">Manage who has access to participate in your project.</p>
        </div>
        <button
          onClick={() => setIsInviteModalOpen(true)}
          className="flex items-center gap-2 bg-action text-white text-sm font-medium py-2.5 px-5 rounded-lg hover:bg-action/90 active:scale-95 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-action"
        >
          <PlusIcon className="w-5 h-5" />
          Add Participants
        </button>
      </div>

      {/* Join Link Section */}
      <div className="bg-app-bg border border-app-border/80 rounded-2xl p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Share Join Link</h3>
            <p className="text-text-muted text-[13px] leading-relaxed mb-4">
              Share this link via WhatsApp, email, or any messaging app. Anyone with this link can join your project.
            </p>
            <div className="flex items-center gap-2 bg-white border border-app-border rounded-xl p-3 shadow-sm">
              <input
                type="text"
                readOnly
                value={projectLink}
                className="flex-1 bg-transparent text-action text-sm font-mono selection:bg-action/10 outline-none"
              />
              <button
                onClick={handleCopyLink}
                className="flex-shrink-0 bg-action/10 text-action text-sm font-medium px-4 py-2 rounded-lg hover:bg-action/15 active:scale-95 transition-colors"
              >
                {linkCopied ? '✓ Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Groups ({groups.length})</h3>
          <button
            onClick={() => onOpenGroupModal()}
            className="flex items-center gap-2 text-[11px] font-bold text-action/60 hover:text-action p-2 rounded-md hover:bg-app-bg active:bg-neutral-100 uppercase tracking-wider transition-colors"
          >
            <FolderPlusIcon className="w-5 h-5" />
            New Group
          </button>
        </div>
        {groups.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {groups.map(group => (
              <GroupCard
                key={group.id}
                group={group}
                participants={participants}
                onEdit={() => onOpenGroupModal(group)}
                onDelete={() => handleDeleteClick('group', group.id, group.name)}
                onClick={() => onOpenGroupModal(group)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 border-2 border-dashed border-app-border rounded-lg">
            <FolderPlusIcon className="w-10 h-10 mx-auto text-text-faint" />
            <h4 className="mt-4 font-semibold text-text-ink">No groups created yet</h4>
            <p className="mt-1 text-sm text-text-muted">Use groups to organize participants and assign tasks.</p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-app-border">
        <div className="p-4 sm:p-5 border-b border-app-border bg-app-bg/30">
          <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">All Participants ({participants.length})</h3>
        </div>
        {participants.length > 0 ? (
          <ul className="divide-y divide-app-border">
            {participants.map(participant => (
              <li key={participant.id}>
                <div onClick={() => onViewProfile(participant)} className="w-full text-left p-4 flex items-center justify-between hover:bg-app-bg transition-colors cursor-pointer group">
                  <div className="flex items-center gap-4">
                    <Avatar name={participant.name} avatarUrl={participant.avatarUrl} id={participant.id} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <p className="text-[13px] font-bold text-text-ink">{participant.name}</p>
                      <p className="text-[11px] font-medium text-text-muted opacity-80 uppercase tracking-wider">Status: {participant.status}</p>
                    </div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteClick('participant', participant.id, participant.name) }} className="text-sm font-medium text-status-warn hover:text-status-warn p-2 rounded-md hover:bg-status-warn-bg active:bg-status-warn-bg opacity-0 group-hover:opacity-100 transition-opacity">
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center py-12">
            <UsersIcon className="w-12 h-12 mx-auto text-text-faint mb-4" />
            <h4 className="mt-4 font-semibold text-text-ink">No participants yet</h4>
            <p className="mt-1 text-sm text-text-muted mb-6">Invite participants to join your research project.</p>
            <button
              onClick={() => setIsInviteModalOpen(true)}
              className="inline-flex items-center gap-2 bg-white border border-app-border text-text-ink text-sm font-medium py-2 px-4 rounded-lg hover:bg-app-bg active:scale-95 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-action"
            >
              <PlusIcon className="w-5 h-5" />
              Add Participants
            </button>
          </div>
        )}
      </div>

      {invitations.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-app-border">
          <div className="p-4 sm:p-6 border-b border-app-border">
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Pending Invitations ({invitations.length})</h3>
          </div>
          <ul className="divide-y divide-app-border">
            {invitations.map(invite => (
              <li key={invite.id} className="p-4 flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-sm font-medium text-text-ink">{invite.email}</p>
                  <p className="text-sm text-text-muted">Invited {new Date(invite.invitedAt).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => alert(`Resending invite to ${invite.email}`)} className="flex items-center gap-2 text-sm font-medium text-status-warn hover:text-status-warn p-2 rounded-md hover:bg-status-warn-bg active:bg-status-warn-bg">
                    <SendIcon className="w-4 h-4" /> Resend
                  </button>
                  <button onClick={() => handleDeleteClick('invite', invite.id, invite.email)} className="text-sm font-medium text-status-warn hover:text-status-warn p-2 rounded-md hover:bg-status-warn-bg active:bg-status-warn-bg">
                    Revoke
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isInviteModalOpen && (
        <AddParticipantsModal
          onClose={() => setIsInviteModalOpen(false)}
          onInvite={onInvite}
          projectLink={projectLink}
        />
      )}



      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={deleteModalContent.title}
        message={deleteModalContent.message}
        confirmButtonText={deleteModalContent.confirmText}
      />
    </div>
  );
};

export default ParticipantsView;
